import { Component, type ErrorInfo, type ReactNode } from 'react'

import { captureException } from './sentry'

// React render errors do not bubble up to window.onerror or unhandled
// rejection handlers — Sentry's browserTracing integration cannot see them
// on its own. The class-component ErrorBoundary intercepts them via
// componentDidCatch and forwards to captureException, which is a no-op when
// Sentry is disabled (see lib/sentry.ts). The fallback UI is rendered when
// any descendant component throws during render, so users see a recoverable
// "Something went wrong" screen instead of a white page.
//
// Intentionally written as a class component — React still requires class
// components for error boundaries (no hook equivalent as of React 19).
// Keeping the implementation here in lib/ rather than reaching for
// @sentry/react's <Sentry.ErrorBoundary> avoids a static `@sentry/react`
// import that would defeat the dynamic-import tree-shaking in lib/sentry.ts.

type Props = {
  children: ReactNode
  fallback: ReactNode
}

type State = {
  hasError: boolean
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false }

  static getDerivedStateFromError(): State {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // captureException is a no-op when Sentry is disabled — calling it
    // unconditionally is the correct shape for opt-in observability.
    // info.componentStack is intentionally not forwarded yet — Sentry's SDK
    // already captures the JS stack; the React-specific component stack
    // becomes useful once we add explicit context/tags around it.
    void info
    captureException(error)
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return this.props.fallback
    }
    return this.props.children
  }
}
